import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@/shared/components';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  userLabel: string;
  children: ReactNode;
  confirmLabel: string;
  confirmVariant: 'destructive' | 'brand';
  loading?: boolean;
  onSubmit: (credentials: { password: string; code: string }) => void;
}

/**
 * Eine Kontoaktion an einem fremden Konto, bestätigt mit eigenem Passwort und frischem
 * Authenticator-Code. Der Server prüft beides; die Felder leeren sich bei jedem Öffnen, damit kein
 * Code aus einem früheren Versuch stehen bleibt — er gilt ohnehin nur einmal.
 */
export function UserReauthDialog({
  open,
  onOpenChange,
  title,
  userLabel,
  children,
  confirmLabel,
  confirmVariant,
  loading,
  onSubmit,
}: Props) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    if (open) {
      setPassword('');
      setCode('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{userLabel}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="text-ink-soft mb-5 text-sm">{children}</div>
          <div className="grid gap-4">
            <FormField label="Ihr aktuelles Passwort" required>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FormField>
            <FormField label="Ihr 6-stelliger Authenticator-Code" required>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </FormField>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant={confirmVariant}
            loading={loading}
            disabled={!password.trim() || !code.trim()}
            onClick={() => onSubmit({ password, code })}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
